import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { 
  Bell, 
  MessageSquare, 
  Users, 
  FileText, 
  AlertCircle, 
  Calendar,
  Clock,
  Settings,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  Trash,
  Edit,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  Mail,
  Send
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TemplateSetLoader } from './TemplateSetLoader';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { notificationService } from '@/services/notificationService';
import { NotificationTemplateEditor } from './NotificationTemplateEditor';
import { testDataGenerator } from '@/utils/testDataGenerator';
import { renderTemplate } from '@/services/notificationTemplateService';
import { useTenant } from '@/contexts/TenantContext';

interface NotificationTemplate {
  id: string;
  notification_type: string;
  title_template: string;
  message_template: string;
  email_subject_template?: string;
  email_body_template?: string;
  sms_template?: string;
  slack_template?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  description?: string;
  variables?: string[];
}

interface NotificationType {
  type: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  variables: string[];
}

const NOTIFICATION_TYPES: NotificationType[] = [
  // Project-related notifications
  {
    type: 'project_created',
    name: 'Project Created',
    description: 'When a new project is created',
    icon: <FileText className="h-4 w-4" />,
    category: 'Projects',
    variables: ['project_name', 'project_type', 'customer_name', 'created_by']
  },

  {
    type: 'organization_created',
    name: 'Organization Created',
    description: 'When a new organization is created',
    icon: <FileText className="h-4 w-4" />,
    category: 'Organizations',
    variables: ['organization_name', 'organization_type', 'pipeline_name', 'owner_name', 'owner_email']
  },
  

  
  // Communication notifications
  {
    type: 'project_mention',
    name: 'Project Mention',
    description: 'When someone mentions you in a project comment',
    icon: <MessageSquare className="h-4 w-4" />,
    category: 'Projects',
    variables: ['mentioned_by', 'project_name', 'comment_text', 'project_type']
  },
  {
    type: 'ticket_mention',
    name: 'Ticket Mention',
    description: 'When someone mentions you in a ticket comment',
    icon: <MessageSquare className="h-4 w-4" />,
    category: 'Support',
    variables: ['mentioned_by', 'ticket_title', 'comment_text', 'ticket_id']
  },

  {
    type: 'project_comment',
    name: 'Project Comment',
    description: 'When someone comments on a project',
    icon: <MessageSquare className="h-4 w-4" />,
    category: 'Projects',
    variables: ['sender_name', 'project_name', 'comment_text', 'project_type']
  },
  {
    type: 'project_update',
    name: 'Project Update',
    description: 'When a project is updated',
    icon: <FileText className="h-4 w-4" />,
    category: 'Projects',
    variables: ['sender_name', 'project_name', 'project_type', 'update_details']
  },
  {
    type: 'project_stage_move',
    name: 'Project Stage Move',
    description: 'When a project moves to a new stage',
    icon: <FileText className="h-4 w-4" />,
    category: 'Projects',
    variables: ['sender_name', 'project_name', 'old_stage', 'new_stage', 'project_type']
  },
  {
    type: 'project_follower',
    name: 'Project Follower',
    description: 'When someone follows a project',
    icon: <Users className="h-4 w-4" />,
    category: 'Projects',
    variables: ['follower_name', 'project_name', 'project_type']
  },
  
  // Support notifications
  {
    type: 'ticket_created',
    name: 'Ticket Created',
    description: 'When a new support ticket is created',
    icon: <AlertCircle className="h-4 w-4" />,
    category: 'Support',
    variables: ['ticket_title', 'ticket_id', 'created_by', 'priority']
  },
  {
    type: 'ticket_comment',
    name: 'Ticket Comment',
    description: 'When someone comments on a support ticket',
    icon: <MessageSquare className="h-4 w-4" />,
    category: 'Support',
    variables: ['sender_name', 'ticket_title', 'comment_text', 'ticket_id']
  },
  {
    type: 'ticket_updated',
    name: 'Ticket Updated',
    description: 'When a support ticket is updated',
    icon: <AlertCircle className="h-4 w-4" />,
    category: 'Support',
    variables: ['sender_name', 'ticket_title', 'ticket_id', 'update_details']
  },
  {
    type: 'ticket_resolved',
    name: 'Ticket Resolved',
    description: 'When a support ticket is resolved',
    icon: <AlertCircle className="h-4 w-4" />,
    category: 'Support',
    variables: ['sender_name', 'ticket_title', 'ticket_id', 'resolution_details']
  },

  
  // Scheduling notifications
  {
    type: 'booking_scheduled_attendee',
    name: 'Booking Scheduled (Attendee)',
    description: 'When a new appointment is scheduled - sent to the attendee/customer',
    icon: <Calendar className="h-4 w-4" />,
    category: 'Scheduling',
    variables: ['booking_date', 'booking_time', 'host_name', 'project_name', 'meeting_link']
  },
  {
    type: 'booking_scheduled_host',
    name: 'Booking Scheduled (Host)',
    description: 'When a new appointment is scheduled - sent to the host/team member',
    icon: <Calendar className="h-4 w-4" />,
    category: 'Scheduling',
    variables: ['booking_date', 'booking_time', 'customer_name', 'project_name', 'meeting_link', 'custom_questions_html', 'custom_questions_text', 'has_custom_questions']
  },

  {
    type: 'booking_reminder_1hr_attendee',
    name: '1 Hour Reminder (Attendee)',
    description: 'Reminder sent 1 hour before appointment to the attendee/customer',
    icon: <Clock className="h-4 w-4" />,
    category: 'Scheduling',
    variables: ['booking_date', 'booking_time', 'host_name', 'project_name', 'meeting_link', 'custom_questions_html', 'custom_questions_text', 'has_custom_questions']
  },
  {
    type: 'booking_reminder_1hr_host',
    name: '1 Hour Reminder (Host)',
    description: 'Reminder sent 1 hour before appointment to the host/team member',
    icon: <Clock className="h-4 w-4" />,
    category: 'Scheduling',
    variables: ['booking_date', 'booking_time', 'customer_name', 'project_name', 'meeting_link', 'custom_questions_html', 'custom_questions_text', 'has_custom_questions']
  },
  {
    type: 'booking_reminder_5min_attendee',
    name: '5 Minute Reminder (Attendee)',
    description: 'Reminder sent 5 minutes before appointment to the attendee/customer',
    icon: <Clock className="h-4 w-4" />,
    category: 'Scheduling',
    variables: ['booking_date', 'booking_time', 'customer_name', 'project_name', 'meeting_link', 'custom_questions_html', 'custom_questions_text', 'has_custom_questions']
  },
  {
    type: 'booking_reminder_5min_host',
    name: '5 Minute Reminder (Host)',
    description: 'Reminder sent 5 minutes before appointment to the host/team member',
    icon: <Clock className="h-4 w-4" />,
    category: 'Scheduling',
    variables: ['booking_date', 'booking_time', 'customer_name', 'project_name', 'meeting_link', 'custom_questions_html', 'custom_questions_text', 'has_custom_questions']
  },

  // Task-related notifications
  {
    type: 'task_assigned',
    name: 'Task Assigned',
    description: 'When a task is assigned to you',
    icon: <FileText className="h-4 w-4" />,
    category: 'Tasks',
    variables: ['sender_name', 'task_name', 'due_date', 'task_description']
  },
  {
    type: 'task_updated',
    name: 'Task Updated',
    description: 'When a task assigned to you is updated',
    icon: <FileText className="h-4 w-4" />,
    category: 'Tasks',
    variables: ['sender_name', 'task_name', 'update_details']
  },
  {
    type: 'task_completed',
    name: 'Task Completed',
    description: 'When a task is marked as completed',
    icon: <FileText className="h-4 w-4" />,
    category: 'Tasks',
    variables: ['sender_name', 'task_name', 'completion_date']
  },
  {
    type: 'task_comment',
    name: 'Task Comment',
    description: 'When someone comments on a task',
    icon: <MessageSquare className="h-4 w-4" />,
    category: 'Tasks',
    variables: ['sender_name', 'task_name', 'comment_text']
  },

  // User management notifications
  {
    type: 'user_welcome',
    name: 'User Welcome',
    description: 'When a new user account is created',
    icon: <Users className="h-4 w-4" />,
    category: 'Users',
    variables: ['first_name', 'last_name', 'email', 'temp_password', 'login_url', 'company_name']
  },

  // Contract notifications
  {
    type: 'contract_reminder',
    name: 'Contract Reminder',
    description: 'Reminder sent to customers for pending contract signatures',
    icon: <FileText className="h-4 w-4" />,
    category: 'Contracts',
    variables: ['contract_name', 'customer_name', 'signing_link', 'company_name', 'sender_name', 'sender_email']
  }
];

const CHANNELS = [
  { id: 'in_app', name: 'In-App', icon: <Bell className="h-4 w-4" /> },
  { id: 'email', name: 'Email', icon: <MessageSquare className="h-4 w-4" /> },
  { id: 'sms', name: 'SMS', icon: <MessageSquare className="h-4 w-4" /> },
  { id: 'slack', name: 'Slack', icon: <MessageSquare className="h-4 w-4" /> }
];

export function NotificationTemplates() {
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'category' | 'updated'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [previewData, setPreviewData] = useState<any>({});
  const [sendingTestEmail, setSendingTestEmail] = useState<string | null>(null);
  const { currentTenant } = useTenant();

  useEffect(() => {
    loadTemplates();
  }, [currentTenant?.tenant_id]); // Refresh when tenant changes

  const loadTemplates = async () => {
    try {
      setLoading(true);
      
      // Get current tenant ID
      const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
      if (tenantError) {
        console.error('Error getting tenant ID:', tenantError);
        toast.error('Failed to get tenant ID');
        return;
      }
      
      const { data, error } = await supabase
        .from('notification_templates')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('notification_type');
      
      if (error) throw error;
      
      setTemplates((data || []) as unknown as NotificationTemplate[]);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load notification templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async (template: NotificationTemplate) => {
    try {
      // Only send updatable fields to Supabase
      const updateData = {
        title_template: template.title_template,
        message_template: template.message_template,
        email_subject_template: template.email_subject_template,
        email_body_template: template.email_body_template,
        sms_template: template.sms_template,
        slack_template: template.slack_template,
        is_active: template.is_active,
        description: template.description,
        variables: template.variables
      };

      let result;
      
      // Check if this is a new template (placeholder ID) or existing template
      if (template.id.startsWith('placeholder-')) {
        // Get current tenant ID for new template
        const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
        if (tenantError) {
          console.error('Error getting tenant ID:', tenantError);
          toast.error('Failed to get tenant ID');
          return;
        }

        // Create new template
        const createData = {
          notification_type: template.notification_type,
          tenant_id: tenantId,
          ...updateData
        };
        
        result = await supabase
          .from('notification_templates')
          .insert([createData])
          .select();
      } else {
        // Update existing template
        result = await supabase
          .from('notification_templates')
          .update(updateData)
          .eq('id', template.id)
          .select();
      }

      if (result.error) {
        toast.error('Failed to save template');
        return;
      }

      toast.success('Template saved successfully');
      await loadTemplates();
      setEditingTemplate(null);
      setIsEditDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleToggleTemplate = async (id: string, isActive: boolean) => {
    try {
      // For placeholder templates, we need to create them first
      if (id.startsWith('placeholder-')) {
        const notificationType = id.replace('placeholder-', '');
        
        const typeInfo = NOTIFICATION_TYPES.find(t => t.type === notificationType);
        
        if (!typeInfo) {
          toast.error('Template type not found');
          return;
        }

        // Get current tenant ID for new template
        const { data: tenantId, error: tenantError } = await supabase.rpc('get_current_user_tenant_id');
        if (tenantError) {
          console.error('Error getting tenant ID:', tenantError);
          toast.error('Failed to get tenant ID');
          return;
        }

        const createData = {
          notification_type: notificationType,
          tenant_id: tenantId,
          title_template: `{${typeInfo.variables[0] || 'title'}}`,
          message_template: `{${typeInfo.variables[0] || 'message'}}`,
          email_subject_template: `{${typeInfo.variables[0] || 'subject'}}`,
          email_body_template: '',
          sms_template: '',
          slack_template: '',
          is_active: !isActive,
          description: typeInfo.description,
          variables: typeInfo.variables
        };

        const { error } = await supabase
          .from('notification_templates')
          .insert([createData]);

        if (error) throw error;
      } else {
        // Update existing template
        const { error } = await supabase
          .from('notification_templates')
          .update({ is_active: !isActive })
          .eq('id', id);
        
        if (error) throw error;
      }
      
      await loadTemplates();
      toast.success(`Template ${!isActive ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Error toggling template:', error);
      toast.error('Failed to toggle template');
    }
  };

  const handleEditTemplate = (template: NotificationTemplate) => {
    setEditingTemplate(template);
    setIsEditDialogOpen(true);
  };



  const handlePreviewTemplate = (template: NotificationTemplate) => {
    setPreviewData(testDataGenerator.generateSampleDataForType(template.notification_type));
    setEditingTemplate(template);
    setIsPreviewDialogOpen(true);
  };

  const handleSendTestEmail = async (template: NotificationTemplate) => {
    if (!currentTenant?.tenant_id) {
      toast.error('No tenant selected');
      return;
    }

    setSendingTestEmail(template.id);
    
    try {
      // Get current user's email
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        toast.error('No user email found');
        return;
      }

      // Generate test data for the template
      const testData = testDataGenerator.generateSampleDataForType(template.notification_type);
      
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      // Call Python backend to send test notification email
      const response = await fetch(
        `${import.meta.env.VITE_PYTHON_BACKEND_URL || "http://localhost:8000"}/api/v1/email/send-notification-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            tenant_id: currentTenant.tenant_id,
            notification_type: template.notification_type,
            to_email: user.email,
            template_variables: testData
          }),
        }
      );

      const result = await response.json();

      if (result.status === "success") {
        toast.success(`Test email sent to ${user.email}!`);
      } else {
        throw new Error(result.message || "Failed to send test email");
      }
    } catch (error) {
      console.error("Error sending test email:", error);
      toast.error("Failed to send test email. Check your email configuration.");
    } finally {
      setSendingTestEmail(null);
    }
  };

  // Add a per-template test button
  const handleSendTestNotificationForType = async (template: NotificationTemplate) => {
    if (!currentTenant?.tenant_id) {
      toast.error('No tenant selected');
      return;
    }

    try {
      setSendingTestEmail(template.id); // Set loading state
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        toast.error('No user email found');
        setSendingTestEmail(null);
        return;
      }
      
      // Generate realistic test data
      const sampleData = await testDataGenerator.generateSampleDataForType(template.notification_type);
      const { title, message } = await testDataGenerator.generateRealisticNotification(template.notification_type, sampleData);
      

      // Get auth session for API calls
      const { data: { session } } = await supabase.auth.getSession();
      
      // Create in-app notification using edge function
      const notificationResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          user_id: user.id,
          type: template.notification_type,
          title: title,
          message: message,
          metadata: sampleData,
          sender_id: sampleData.sender_id,
          sender_name: sampleData.sender_name,
          tenant_id: currentTenant?.tenant_id
        })
      });

      if (!notificationResponse.ok) {
        throw new Error('Failed to create notification');
      }

      // Email sending is automatically handled by the database trigger

      // Send Slack notification if enabled
      try {
        await supabase.functions.invoke('send-slack-notification', {
          body: {
            templateType: template.notification_type,
            userId: user.id,
            data: sampleData,
            tenantId: currentTenant.tenant_id,
          }
        });
      } catch (slackError) {
        console.error('Error sending test Slack notification:', slackError);
        // Don't fail the whole process if Slack isn't configured
      }
      
      toast.success('Test notification sent to all platforms!');
    } catch (error) {
      toast.error('Failed to send test notification');
      console.error('Error sending test notification:', error);
    } finally {
      setSendingTestEmail(null); // Clear loading state
    }
  };

  const renderTemplatePreview = (template: NotificationTemplate, data: any) => {
    const replaceVariables = (text: string) => {
      return renderTemplate(text, data, template.notification_type);
    };

    return (
      <div className="space-y-4">
        <div>
          <Label className="text-sm font-medium text-muted-foreground">Title</Label>
          <div className="mt-1 p-3 bg-muted rounded-md">
            {replaceVariables(template.title_template)}
          </div>
        </div>
        <div>
          <Label className="text-sm font-medium text-muted-foreground">Message</Label>
          <div className="mt-1 p-3 bg-muted rounded-md">
            {replaceVariables(template.message_template)}
          </div>
        </div>
        {template.email_subject_template && (
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Email Subject</Label>
            <div className="mt-1 p-3 bg-muted rounded-md">
              {replaceVariables(template.email_subject_template)}
            </div>
          </div>
        )}
        {template.email_body_template && (
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Email Body</Label>
            <div className="mt-1 p-3 bg-muted rounded-md max-h-32 overflow-y-auto">
              {replaceVariables(template.email_body_template)}
            </div>
          </div>
        )}
        {template.sms_template && (
          <div>
            <Label className="text-sm font-medium text-muted-foreground">SMS</Label>
            <div className="mt-1 p-3 bg-muted rounded-md">
              {replaceVariables(template.sms_template)}
            </div>
          </div>
        )}
        {template.slack_template && (
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Slack</Label>
            <div className="mt-1 p-3 bg-muted rounded-md">
              {replaceVariables(template.slack_template)}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Create a combined list of all notification types, including those not in database
  const allNotificationTypes = NOTIFICATION_TYPES.map(typeInfo => {
    const existingTemplate = templates.find(t => t.notification_type === typeInfo.type);
    if (existingTemplate) {
      return existingTemplate;
    } else {
      // Create a placeholder template for types not in database
      return {
        id: `placeholder-${typeInfo.type}`,
        notification_type: typeInfo.type,
        title_template: `{${typeInfo.variables[0] || 'title'}}`,
        message_template: `{${typeInfo.variables[0] || 'message'}}`,
        email_subject_template: `{${typeInfo.variables[0] || 'subject'}}`,
        email_body_template: '',
        sms_template: '',
        slack_template: '',
        is_active: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        description: typeInfo.description,
        variables: typeInfo.variables
      } as NotificationTemplate;
    }
  });

  const filteredTemplates = allNotificationTypes.filter(template => {
    const typeInfo = NOTIFICATION_TYPES.find(t => t.type === template.notification_type);
    const matchesCategory = selectedCategory === 'all' || typeInfo?.category === selectedCategory;
    const matchesSearch = template.notification_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         typeInfo?.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    const typeInfoA = NOTIFICATION_TYPES.find(t => t.type === a.notification_type);
    const typeInfoB = NOTIFICATION_TYPES.find(t => t.type === b.notification_type);
    
    switch (sortBy) {
      case 'name':
        return sortOrder === 'asc' 
          ? (typeInfoA?.name || '').localeCompare(typeInfoB?.name || '')
          : (typeInfoB?.name || '').localeCompare(typeInfoA?.name || '');
      case 'category':
        return sortOrder === 'asc'
          ? (typeInfoA?.category || '').localeCompare(typeInfoB?.category || '')
          : (typeInfoB?.category || '').localeCompare(typeInfoA?.category || '');
      case 'updated':
        return sortOrder === 'asc'
          ? new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()
          : new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      default:
        return 0;
    }
  });

  const categories = Array.from(new Set(NOTIFICATION_TYPES.map(t => t.category)));

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading notification templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        {/* Title and Action Buttons */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Notification Templates</h2>
            <p className="text-muted-foreground">
              Customize the content of notifications sent to users
            </p>
          </div>
          <div className="flex items-center gap-3">
            <TemplateSetLoader onTemplatesLoaded={loadTemplates} />
            <Button onClick={() => loadTemplates()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Search, Filters, and Sort */}
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="h-4 w-4 text-muted-foreground" />
            </div>
            <Input
              type="search"
              placeholder="Search templates..."
              className="pl-10 h-10 border-border bg-muted focus:bg-background"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-48 h-10">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(category => (
                <SelectItem key={category} value={category}>{category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={(value) => setSortBy(value as any)}>
            <SelectTrigger className="w-48 h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sort by Name</SelectItem>
              <SelectItem value="category">Sort by Category</SelectItem>
              <SelectItem value="updated">Sort by Updated</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="h-10 w-10"
          >
            {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid gap-4">
        {sortedTemplates.length === 0 ? (
          <Card>
            <CardContent className="pt-6 pb-6">
              <div className="text-center py-8">
                <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2 text-foreground">No templates found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || selectedCategory !== 'all' 
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Get started by loading professional notification templates or create your own.'
                  }
                </p>
                {!searchTerm && selectedCategory === 'all' && (
                  <div className="flex justify-center">
                    <TemplateSetLoader onTemplatesLoaded={loadTemplates} />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          sortedTemplates.map(template => {
            const typeInfo = NOTIFICATION_TYPES.find(t => t.type === template.notification_type);
            return (
              <Card key={template.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        {typeInfo?.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <CardTitle className="text-lg text-foreground">{typeInfo?.name || template.notification_type}</CardTitle>
                          <Badge variant={template.is_active ? "default" : "secondary"}>
                            {template.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {typeInfo?.description}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                          <span>Category: {typeInfo?.category}</span>
                          <span>Updated: {new Date(template.updated_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePreviewTemplate(template)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Preview Template</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      {template.email_subject_template && template.email_body_template && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleSendTestNotificationForType(template)}
                                disabled={sendingTestEmail === template.id}
                              >
                                {sendingTestEmail === template.id ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Send className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Test Notification</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditTemplate(template)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit Template</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Switch
                        checked={template.is_active}
                        onCheckedChange={() => handleToggleTemplate(template.id, template.is_active)}
                      />
                    </div>
                  </div>
                </CardHeader>
              </Card>
            );
          })
        )}
      </div>



      {/* Edit Template Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate?.id ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
            <DialogDescription>
              Customize the notification content for different channels
            </DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <TemplateEditForm
              template={editingTemplate}
              onSave={handleSaveTemplate}
              onCancel={() => {
                setIsEditDialogOpen(false);
                setEditingTemplate(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Template Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
            <DialogDescription>
              See how your template will look with sample data
            </DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4">
              {renderTemplatePreview(editingTemplate, previewData)}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface TemplateEditFormProps {
  template: NotificationTemplate;
  onSave: (template: NotificationTemplate) => void;
  onCancel: () => void;
}

function TemplateEditForm({ template, onSave, onCancel }: TemplateEditFormProps) {
  const [formData, setFormData] = useState(template);
  const [activeTab, setActiveTab] = useState('in_app');
  const typeInfo = NOTIFICATION_TYPES.find(t => t.type === template.notification_type);

  useEffect(() => {
    // Removed debug log
  }, [template]);

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Notification Type</Label>
          <Input
            value={typeInfo?.name || template.notification_type}
            disabled
            className="bg-muted"
          />
        </div>
        <div>
          <Label>Status</Label>
          <div className="flex items-center space-x-2 mt-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <span className="text-sm">{formData.is_active ? 'Active' : 'Inactive'}</span>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          {CHANNELS.map(channel => (
            <TabsTrigger key={channel.id} value={channel.id} className="flex items-center space-x-2">
              {channel.icon}
              <span className="hidden sm:inline">{channel.name}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="in_app" className="space-y-4">
          <div>
            <Label>Title Template</Label>
            <Input
              value={formData.title_template}
              onChange={(e) => setFormData({ ...formData, title_template: e.target.value })}
              placeholder="Enter title template..."
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use variables like {'{project_name}'}, {'{customer_name}'}, etc.
            </p>
          </div>
          <div>
            <Label>Message Template</Label>
            <Textarea
              value={formData.message_template}
              onChange={(e) => setFormData({ ...formData, message_template: e.target.value })}
              placeholder="Enter message template..."
              rows={4}
            />
          </div>
        </TabsContent>

        <TabsContent value="email" className="space-y-4">
          <div>
            <Label>Email Subject Template</Label>
            <Input
              value={formData.email_subject_template || ''}
              onChange={(e) => setFormData({ ...formData, email_subject_template: e.target.value })}
              placeholder="Enter email subject template..."
            />
          </div>
          <div>
            <Label>Email Body Template</Label>
            <NotificationTemplateEditor
              value={formData.email_body_template || ''}
              onChange={(html) => setFormData({ ...formData, email_body_template: html })}
              variables={typeInfo?.variables || []}
              placeholder="Enter email body template with rich formatting..."
            />
          </div>
        </TabsContent>

        <TabsContent value="sms" className="space-y-4">
          <div>
            <Label>SMS Template</Label>
            <Textarea
              value={formData.sms_template || ''}
              onChange={(e) => setFormData({ ...formData, sms_template: e.target.value })}
              placeholder="Enter SMS template..."
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Keep SMS messages short and concise (160 characters or less)
            </p>
          </div>
        </TabsContent>

        <TabsContent value="slack" className="space-y-4">
          <div>
            <Label>Slack Template</Label>
            <Textarea
              value={formData.slack_template || ''}
              onChange={(e) => setFormData({ ...formData, slack_template: e.target.value })}
              placeholder="Enter Slack template..."
              rows={4}
            />
            <p className="text-xs text-muted-foreground mt-1">
              You can use Slack formatting like *bold*, _italic_, and {'<link|text>'}
            </p>
          </div>
        </TabsContent>
      </Tabs>

      {typeInfo?.variables && typeInfo.variables.length > 0 && (
        <div>
          <Label>Available Variables</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {typeInfo.variables.map(variable => (
              <Badge key={variable} variant="outline" className="text-xs">
                {`{${variable}}`}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave}>
          <Save className="h-4 w-4 mr-2" />
          Save Template
        </Button>
      </DialogFooter>
    </div>
  );
} 